import React, { useMemo, useState } from "react"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import Card from "@react/shared/ui/Card"
import { patchRequest, postRequest } from "@react/helpers/fetch"

const formatAmountDecimal = (cents, symbol = "€") => {
  const value = (cents / 100).toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
  return `${value} ${symbol}`
}

const formatDate = value => {
  if (!value) return "—"
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return value
  const day   = String(date.getDate()).padStart(2, "0")
  const month = String(date.getMonth() + 1).padStart(2, "0")
  const year  = date.getFullYear()
  return `${day}/${month}/${year}`
}

const formatStatus = status => I18n.t(`projects.situations.history.statuses.${status}`, { defaultValue: status })

const goTo = (event, url) => {
  if (event.target.closest("a, button")) return
  if (url) window.location.href = url
}

// ─── Visa workflow (presented → admitted → certified) ─────────────────────

const VISA_TONES = {
  presented: "bg-amber-50 text-amber-700 border-amber-200",
  admitted:  "bg-blue-50 text-blue-700 border-blue-100",
  certified: "bg-emerald-50 text-emerald-700 border-emerald-100",
}

const VisaBadge = ({ visa }) => {
  if (!visa?.status) return <span className="text-slate-300">—</span>
  return (
    <span
      title={visa.comment || undefined}
      className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium border ${VISA_TONES[visa.status]}`}
    >
      {I18n.t(`projects.situations.visa.statuses.${visa.status}`)}
    </span>
  )
}

// Admission: line by line when the detail is available (each line's admitted %
// is bounded between the previous cumulative and the presented %), otherwise
// a global amount (historical situations without detail).
const AdmitModal = ({ invoice, currencySymbol, onCancel, onConfirm, submitting }) => {
  const lines = invoice.visa?.lines || []
  const lineMode = lines.length > 0

  const [amount, setAmount]     = useState((invoice.subtotal_cents / 100).toFixed(2))
  const [comment, setComment]   = useState("")
  const [admitted, setAdmitted] = useState(() =>
    Object.fromEntries(lines.map(line => [line.qline_id, line.presented_pct]))
  )

  const lineComputation = useMemo(() => {
    if (!lineMode) return null
    let admittedWorks = 0
    let presentedWorks = 0
    const rows = lines.map(line => {
      const pct = Math.min(line.presented_pct, Math.max(line.previous_pct, Number(admitted[line.qline_id]) || 0))
      const monthCents = Math.round((line.contract_cents * (pct - line.previous_pct)) / 100)
      admittedWorks  += monthCents
      presentedWorks += line.month_cents
      return { ...line, admittedPct: pct, admittedMonthCents: monthCents }
    })
    const deductions = invoice.subtotal_cents - presentedWorks
    return { rows, totalCents: admittedWorks + deductions, varianceCents: admittedWorks - presentedWorks }
  }, [lines, admitted, invoice.subtotal_cents, lineMode])

  const globalAmountCents = Math.round(parseFloat(String(amount).replace(",", ".")) * 100)
  const variance = lineMode
    ? lineComputation.varianceCents
    : (Number.isFinite(globalAmountCents) ? globalAmountCents - invoice.subtotal_cents : 0)

  const confirm = () => {
    if (lineMode) {
      onConfirm({
        comment,
        lines: lineComputation.rows.map(row => ({ qline_id: row.qline_id, admitted_percentage: row.admittedPct })),
      })
    } else {
      onConfirm({ comment, admitted_subtotal_cents: globalAmountCents })
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40" onClick={onCancel}>
      <div className={`bg-white rounded-xl shadow-xl ${lineMode ? "w-[680px]" : "w-[440px]"} p-5 max-h-[85vh] overflow-y-auto`} onClick={event => event.stopPropagation()}>
        <h3 className="text-[15px] font-semibold text-slate-900">
          {I18n.t("projects.situations.visa.admit_title", { number: invoice.situation_number })}
        </h3>
        <p className="text-[12.5px] text-slate-500 mt-1">
          {I18n.t("projects.situations.visa.admit_subtitle", { amount: formatAmountDecimal(invoice.subtotal_cents, currencySymbol) })}
        </p>

        {lineMode ? (
          <div className="mt-4 border border-slate-200 rounded-lg overflow-hidden">
            <table className="w-full text-[12px]">
              <thead>
                <tr className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
                  <th className="px-2.5 py-2 text-left">{I18n.t("projects.situations.visa.line")}</th>
                  <th className="px-2.5 py-2 text-right">{I18n.t("projects.situations.visa.previous_pct")}</th>
                  <th className="px-2.5 py-2 text-right">{I18n.t("projects.situations.visa.presented_pct")}</th>
                  <th className="px-2.5 py-2 text-right">{I18n.t("projects.situations.visa.admitted_pct")}</th>
                  <th className="px-2.5 py-2 text-right">{I18n.t("projects.situations.visa.admitted_month")}</th>
                </tr>
              </thead>
              <tbody>
                {lineComputation.rows.map(row => (
                  <tr key={row.qline_id} className="border-t border-slate-100">
                    <td className="px-2.5 py-1.5 max-w-[260px] truncate" title={row.description}>{row.description}</td>
                    <td className="px-2.5 py-1.5 text-right tabular-nums text-slate-400">{row.previous_pct} %</td>
                    <td className="px-2.5 py-1.5 text-right tabular-nums text-slate-600">{row.presented_pct} %</td>
                    <td className="px-2.5 py-1.5 text-right">
                      <input
                        type      = "number"
                        min       = {row.previous_pct}
                        max       = {row.presented_pct}
                        step      = "1"
                        value     = {admitted[row.qline_id]}
                        onChange  = {event => setAdmitted(current => ({ ...current, [row.qline_id]: event.target.value }))}
                        className = {`w-16 h-7 px-1.5 text-right text-[12px] border rounded tabular-nums focus:border-slate-900 focus:ring-0 outline-none ${row.admittedPct < row.presented_pct ? "border-rose-300 bg-rose-50" : "border-slate-200"}`}
                      />
                    </td>
                    <td className={`px-2.5 py-1.5 text-right tabular-nums ${row.admittedMonthCents < row.month_cents ? "text-rose-600" : ""}`}>
                      {formatAmountDecimal(row.admittedMonthCents, currencySymbol)}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="bg-slate-50 border-t border-slate-200 font-semibold">
                  <td className="px-2.5 py-2" colSpan={4}>{I18n.t("projects.situations.visa.admitted_amount")}</td>
                  <td className="px-2.5 py-2 text-right tabular-nums">{formatAmountDecimal(lineComputation.totalCents, currencySymbol)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        ) : (
          <>
            <label className="block mt-4 text-[12px] font-medium text-slate-600">
              {I18n.t("projects.situations.visa.admitted_amount")}
            </label>
            <input
              type      = "number"
              step      = "0.01"
              autoFocus
              value     = {amount}
              onChange  = {event => setAmount(event.target.value)}
              className = "mt-1 w-full border border-slate-200 rounded-lg px-2.5 py-2 text-[13px] text-right tabular-nums focus:outline-none focus:ring-2 focus:ring-blue-200"
            />
          </>
        )}

        {variance !== 0 && (
          <p className={`mt-2 text-[12px] tabular-nums ${variance < 0 ? "text-rose-600" : "text-emerald-600"}`}>
            {I18n.t("projects.situations.visa.variance")} : {variance > 0 ? "+" : ""}{formatAmountDecimal(variance, currencySymbol)}
          </p>
        )}

        <label className="block mt-3 text-[12px] font-medium text-slate-600">
          {I18n.t("projects.situations.visa.comment")}
        </label>
        <textarea
          rows      = {2}
          value     = {comment}
          onChange  = {event => setComment(event.target.value)}
          placeholder = {I18n.t("projects.situations.visa.comment_placeholder")}
          className = "mt-1 w-full border border-slate-200 rounded-lg px-2.5 py-2 text-[13px] focus:outline-none focus:ring-2 focus:ring-blue-200"
        />

        <div className="mt-4 flex justify-end gap-2">
          <button
            type="button"
            className="px-3.5 py-1.5 rounded-lg text-[13px] text-slate-600 hover:bg-slate-100"
            onClick={onCancel}
          >
            {I18n.t("projects.situations.visa.cancel")}
          </button>
          <button
            type="button"
            disabled={submitting || (!lineMode && (!Number.isFinite(globalAmountCents) || globalAmountCents < 0))}
            className="px-3.5 py-1.5 rounded-lg text-[13px] font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-40"
            onClick={confirm}
          >
            {I18n.t("projects.situations.visa.admit_confirm")}
          </button>
        </div>
      </div>
    </div>
  )
}

// Sends the situation to the client / MOE (contract-format PDF attached).
const SendModal = ({ invoice, defaultRecipient, projectName, onCancel, onConfirm, submitting }) => {
  const [recipient, setRecipient] = useState(defaultRecipient || "")
  const [copies, setCopies]       = useState("")
  const [subject, setSubject]     = useState(
    I18n.t("projects.situations.email.default_subject", {
      number:  String(invoice.situation_number).padStart(2, "0"),
      project: projectName,
    })
  )
  const [message, setMessage] = useState(I18n.t("projects.situations.email.default_message"))
  const [includeInvoice, setIncludeInvoice] = useState(false)

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40" onClick={onCancel}>
      <div className="bg-white rounded-xl shadow-xl w-[520px] p-5 max-h-[85vh] overflow-y-auto" onClick={event => event.stopPropagation()}>
        <h3 className="text-[15px] font-semibold text-slate-900">
          {I18n.t("projects.situations.email.title", { number: invoice.situation_number })}
        </h3>
        <p className="text-[12.5px] text-slate-500 mt-1">
          {I18n.t("projects.situations.email.subtitle")}
        </p>

        <label className="block mt-4 text-[12px] font-medium text-slate-600">{I18n.t("projects.situations.email.recipient")}</label>
        <input
          type="text" autoFocus value={recipient}
          onChange={event => setRecipient(event.target.value)}
          placeholder="moe@exemple.fr"
          className="mt-1 w-full border border-slate-200 rounded-lg px-2.5 py-2 text-[13px] focus:outline-none focus:ring-2 focus:ring-blue-200"
        />

        <label className="block mt-3 text-[12px] font-medium text-slate-600">{I18n.t("projects.situations.email.copies")}</label>
        <input
          type="text" value={copies}
          onChange={event => setCopies(event.target.value)}
          placeholder="copie1@exemple.fr, copie2@exemple.fr"
          className="mt-1 w-full border border-slate-200 rounded-lg px-2.5 py-2 text-[13px] focus:outline-none focus:ring-2 focus:ring-blue-200"
        />

        <label className="block mt-3 text-[12px] font-medium text-slate-600">{I18n.t("projects.situations.email.subject")}</label>
        <input
          type="text" value={subject}
          onChange={event => setSubject(event.target.value)}
          className="mt-1 w-full border border-slate-200 rounded-lg px-2.5 py-2 text-[13px] focus:outline-none focus:ring-2 focus:ring-blue-200"
        />

        <label className="block mt-3 text-[12px] font-medium text-slate-600">{I18n.t("projects.situations.email.message")}</label>
        <textarea
          rows={4} value={message}
          onChange={event => setMessage(event.target.value)}
          className="mt-1 w-full border border-slate-200 rounded-lg px-2.5 py-2 text-[13px] focus:outline-none focus:ring-2 focus:ring-blue-200"
        />

        {invoice.number && (
          <label className="mt-3 flex items-center gap-2 text-[12.5px] text-slate-700">
            <input
              type="checkbox" checked={includeInvoice}
              onChange={event => setIncludeInvoice(event.target.checked)}
              className="rounded border-slate-300"
            />
            {I18n.t("projects.situations.email.include_invoice")}
          </label>
        )}

        <div className="mt-4 flex justify-end gap-2">
          <button type="button" className="px-3.5 py-1.5 rounded-lg text-[13px] text-slate-600 hover:bg-slate-100" onClick={onCancel}>
            {I18n.t("projects.situations.visa.cancel")}
          </button>
          <button
            type="button"
            disabled={submitting || !recipient.trim()}
            className="px-3.5 py-1.5 rounded-lg text-[13px] font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-40"
            onClick={() => onConfirm({ recipient, copies, subject, message, include_invoice_pdf: includeInvoice })}
          >
            {I18n.t("projects.situations.email.send")}
          </button>
        </div>
      </div>
    </div>
  )
}

const HistoryTable = ({ history, currencySymbol, emailDefaultRecipient, projectName }) => {
  const [rows, setRows]           = useState(history || [])
  const [admitting, setAdmitting] = useState(null)
  const [sending, setSending]     = useState(null)
  const [submitting, setSubmitting] = useState(false)

  const applyVisa = (invoiceId, visa, extra = {}) => {
    setRows(current => current.map(row => (row.id === invoiceId ? { ...row, ...extra, visa: { ...row.visa, ...visa } } : row)))
  }

  const admit = (invoice, body) => {
    setSubmitting(true)
    patchRequest({ url: invoice.visa.admit_url, body })
      .then(payload => {
        if (payload?.ok) {
          applyVisa(invoice.id, payload.visa)
          setAdmitting(null)
        } else if (payload?.error) {
          flashAlert(payload.error)
        }
        setSubmitting(false)
      })
      .catch(() => setSubmitting(false))
  }

  const certify = invoice => {
    patchRequest({ url: invoice.visa.certify_url, body: {} })
      .then(payload => { if (payload?.ok) applyVisa(invoice.id, payload.visa) })
  }

  const send = (invoice, body) => {
    setSubmitting(true)
    postRequest({ url: invoice.visa.send_email_url, body })
      .then(payload => {
        if (payload?.ok) {
          applyVisa(invoice.id, payload.visa, { sent: true })
          setSending(null)
        } else if (payload?.error) {
          flashAlert(payload.error)
        }
        setSubmitting(false)
      })
      .catch(() => setSubmitting(false))
  }

  if (!rows || rows.length === 0) {
    return (
      <Card className="overflow-hidden">
        <div className="px-5 pt-5 pb-3 border-b border-slate-100">
          <div className="text-[10.5px] uppercase tracking-wider font-semibold text-slate-400 mb-1">
            {I18n.t("projects.situations.history.eyebrow")}
          </div>
          <h3 className="text-[18px] font-semibold text-slate-900">
            {I18n.t("projects.situations.history.title")}
          </h3>
        </div>
        <div className="p-6 text-center text-sm text-slate-500">
          {I18n.t("projects.situations.history.empty")}
        </div>
      </Card>
    )
  }

  return (
    <Card className="overflow-hidden">
      <div className="px-5 pt-5 pb-3 border-b border-slate-100">
        <div className="text-[10.5px] uppercase tracking-wider font-semibold text-slate-400 mb-1">
          {I18n.t("projects.situations.history.eyebrow")}
        </div>
        <h3 className="text-[18px] font-semibold text-slate-900">
          {I18n.t("projects.situations.history.title")}
        </h3>
      </div>
      <div>
        <table className="w-full text-[13px]">
          <thead>
            <tr className="bg-slate-50 text-[10.5px] uppercase tracking-wider text-slate-500 font-semibold">
              <th className="px-3 py-2.5 text-left">{I18n.t("projects.situations.history.number")}</th>
              <th className="px-3 py-2.5 text-left">{I18n.t("projects.situations.history.creation_date")}</th>
              <th className="px-3 py-2.5 text-right">{I18n.t("projects.situations.history.subtotal")}</th>
              <th className="px-3 py-2.5 text-right">{I18n.t("projects.situations.history.total")}</th>
              <th className="px-3 py-2.5 text-left">{I18n.t("projects.situations.history.status")}</th>
              <th className="px-3 py-2.5 text-left">{I18n.t("projects.situations.history.visa")}</th>
              <th className="px-3 py-2.5" />
            </tr>
          </thead>
          <tbody>
            {rows.map(invoice => (
              <tr
                key       = {invoice.id}
                onClick   = {event => goTo(event, invoice.show_url)}
                className = "border-t border-slate-100 cursor-pointer hover:bg-slate-50"
              >
                <td className="px-3 py-2.5 font-medium tabular-nums">{invoice.situation_number}</td>
                <td className="px-3 py-2.5 text-slate-600">{formatDate(invoice.creation_date)}</td>
                <td className="px-3 py-2.5 text-right tabular-nums">{formatAmountDecimal(invoice.subtotal_cents, currencySymbol)}</td>
                <td className="px-3 py-2.5 text-right tabular-nums">{formatAmountDecimal(invoice.total_cents, currencySymbol)}</td>
                <td className="px-3 py-2.5 text-slate-600">{formatStatus(invoice.status)}</td>
                <td className="px-3 py-2.5 whitespace-nowrap">
                  <VisaBadge visa={invoice.visa} />
                  {invoice.visa?.status === "admitted" && invoice.visa.variance_cents !== 0 && invoice.visa.variance_cents != null && (
                    <div className="text-[10.5px] tabular-nums text-rose-600 mt-0.5">
                      {formatAmountDecimal(invoice.visa.variance_cents, currencySymbol)}
                    </div>
                  )}
                  <div className="mt-0.5 flex items-center gap-2">
                    <button
                      type      = "button"
                      onClick   = {() => setSending(invoice)}
                      title     = {I18n.t("projects.situations.email.action_hint")}
                      className = "text-[11px] font-medium text-slate-500 hover:text-slate-900 inline-flex items-center gap-1"
                    >
                      <FontAwesomeIcon icon={invoice.sent ? "envelope-circle-check" : "envelope"} className={invoice.sent ? "text-emerald-600" : ""} />
                      {I18n.t(invoice.sent ? "projects.situations.email.resend_action" : "projects.situations.email.send_action")}
                    </button>
                    {invoice.visa?.status === "presented" && (
                      <button
                        type      = "button"
                        onClick   = {() => setAdmitting(invoice)}
                        className = "text-[11px] font-medium text-blue-700 hover:text-blue-900"
                      >
                        {I18n.t("projects.situations.visa.admit_action")}
                      </button>
                    )}
                    {invoice.visa?.status === "admitted" && (
                      <button
                        type      = "button"
                        onClick   = {() => certify(invoice)}
                        className = "text-[11px] font-medium text-emerald-700 hover:text-emerald-900"
                      >
                        {I18n.t("projects.situations.visa.certify_action")}
                      </button>
                    )}
                  </div>
                </td>
                <td className="px-3 py-2.5 text-right whitespace-nowrap">
                  {invoice.document_url && (
                    <a
                      href      = {invoice.document_url}
                      target    = "_blank"
                      rel       = "noopener"
                      onClick   = {event => event.stopPropagation()}
                      title     = {I18n.t("projects.situations.history.document_hint")}
                      className = "mr-3 text-[11.5px] text-blue-700 hover:text-blue-900 inline-flex items-center gap-1 font-medium"
                    >
                      <FontAwesomeIcon icon="file-contract" />
                      {I18n.t("projects.situations.history.document")}
                    </a>
                  )}
                  <a
                    href      = {invoice.pdf_url}
                    target    = "_blank"
                    rel       = "noopener"
                    onClick   = {event => event.stopPropagation()}
                    className = "text-[11.5px] text-slate-500 hover:text-slate-900 inline-flex items-center gap-1"
                  >
                    <FontAwesomeIcon icon="file-pdf" />
                    PDF
                  </a>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {admitting && (
        <AdmitModal
          invoice        = {admitting}
          currencySymbol = {currencySymbol}
          submitting     = {submitting}
          onCancel       = {() => setAdmitting(null)}
          onConfirm      = {body => admit(admitting, body)}
        />
      )}
      {sending && (
        <SendModal
          invoice          = {sending}
          defaultRecipient = {emailDefaultRecipient}
          projectName      = {projectName}
          submitting       = {submitting}
          onCancel         = {() => setSending(null)}
          onConfirm        = {body => send(sending, body)}
        />
      )}
    </Card>
  )
}

export default HistoryTable
